import React, { useState, forwardRef, useImperativeHandle } from 'react';
import s from './AutoSearch.scss';

const AutoSearch = forwardRef(
  ({ data, text, handleGuessClick, showIncorrectAnswer, isMobile }, ref) => {
    const [query, setQuery] = useState('');
    const [filteredPlayers, setFilteredPlayers] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);

    // Filter players based on query
    const filterPlayers = searchTerm => {
      if (searchTerm.length > 0) {
        // Track all matched items to avoid duplicates
        const matchedItems = new Set();

        // First, find items that start with the query
        const startsWithQuery = data.filter(player => {
          const playerLower = player.toLowerCase();
          const startsWith = playerLower.startsWith(searchTerm);
          if (startsWith) matchedItems.add(player);
          return startsWith;
        });

        // Next, find items where second word starts with query (likely last names)
        const secondNameMatches = data.filter(player => {
          if (matchedItems.has(player)) return false;

          const words = player.split(' ');
          const matchesSecondWord =
            words.length > 1 &&
            words
              .slice(1)
              .some(word => word.toLowerCase().startsWith(searchTerm));

          if (matchesSecondWord) matchedItems.add(player);
          return matchesSecondWord;
        });

        // Finally, find remaining items that contain the query anywhere
        const containsQuery = data.filter(player => {
          if (matchedItems.has(player)) return false;

          const contains = player.toLowerCase().includes(searchTerm);
          if (contains) matchedItems.add(player);
          return contains;
        });

        // Combine all results in priority order and limit to 10 items
        return [
          ...startsWithQuery,
          ...secondNameMatches,
          ...containsQuery,
        ].slice(0, 10);
      }
      return [];
    };

    // Expose methods to parent components
    useImperativeHandle(ref, () => ({
      clearQuery: () => {
        setQuery('');
        setFilteredPlayers([]);
        setShowDropdown(false);
      },
      appendCharacter: character => {
        const newQuery = query + character;
        setQuery(newQuery);

        const searchTerm = newQuery.toLowerCase().trim();
        const filtered = filterPlayers(searchTerm);

        setFilteredPlayers(filtered);
        setShowDropdown(filtered.length > 0);
      },
      handleBackspace: () => {
        const newQuery = query.slice(0, -1);
        setQuery(newQuery);

        if (newQuery.length > 0) {
          const searchTerm = newQuery.toLowerCase().trim();
          const filtered = filterPlayers(searchTerm);

          setFilteredPlayers(filtered);
          setShowDropdown(filtered.length > 0);
        } else {
          setFilteredPlayers([]);
          setShowDropdown(false);
        }
      },
    }));

    const handleInputChange = ({ target: { value } }) => {
      setQuery(value);

      const searchTerm = value.toLowerCase().trim();
      const filtered = filterPlayers(searchTerm);

      setFilteredPlayers(filtered);
      setShowDropdown(filtered.length > 0 && searchTerm.length > 0);
    };

    const handleSelect = player => {
      setQuery(player);
      setShowDropdown(false);
    };

    return (
      <div
        className={`${s.auto_search_container} auto_search_container  ${
          showDropdown ? s.activeDropdown : ''
        } ${showIncorrectAnswer ? 'error' : ''}`}
      >
        <label className={s.auto_search_label} htmlFor="guessPlayer">
          {text}
        </label>
        <div>
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onClick={() => {
              setQuery('');
              setFilteredPlayers([]);
              setShowDropdown(false);
            }}
            className={`${s.auto_search_input} ${
              isMobile ? s.mobile_input : ''
            }`}
            readOnly={false}
            onFocus={isMobile ? e => e.target.blur() : undefined}
            autoFocus={isMobile}
          />
          {isMobile && query.length === 0 && <div className={s.fake_cursor} />}
        </div>
        {showIncorrectAnswer && <i className={s.iconCross} />}

        {showDropdown && (
          <div className={s.auto_search_dropdown}>
            <ul className={s.auto_search_dropdown_list}>
              {filteredPlayers.map((player, index) => (
                <li
                  key={index}
                  onClick={() => handleSelect(player)}
                  className={s.auto_search_item}
                >
                  <span>{player}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {!showIncorrectAnswer && (
          <button
            className={`${s.auto_search_button} ${
              !query.trim() ? s.disable : ''
            }`}
            onClick={() => handleGuessClick(query)}
            disabled={!query.trim()}
          >
            Guess
          </button>
        )}
      </div>
    );
  },
);

export default AutoSearch;
